import React, { useCallback } from 'react';
import {
  Dialog as MuiDialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { tokens } from '../../theme';

/**
 * The one dialog shell.
 *
 * Phase 4 of the design-system plan. The audit found eleven independent
 * overlay implementations and measured two of them properly: a 560px purple
 * panel with radius 8 and 24px padding, and a 444px white one with radius 4
 * and no padding at all. They shared exactly one property — a drop shadow.
 * Neither had a close affordance beyond its Cancel button.
 *
 * This replaces both, and the eight modal components behind them.
 *
 * WHAT IT GUARANTEES, so no caller has to remember
 *
 *   - Four ways out: the cross, the cancel action, Escape, and clicking the
 *     backdrop. Every dialog in the product closes the same way.
 *   - Focus is trapped while open and returns to the trigger on close. MUI's
 *     Dialog does this; the hand-rolled overlays it replaces did not.
 *   - The title is the accessible name, and the body is described by it.
 *   - Nothing silently discards typed work — see `dirty` below.
 *
 * WHY `dirty` EXISTS
 *
 * On /editor-home this dialog opens on page load, so its caller closes by
 * navigating to the dashboard. That is reasonable — there is no page behind
 * it. What is not reasonable is that a stray Escape threw away whatever the
 * author had typed, with no warning and nowhere to get it back. Pass `dirty`
 * and the shell confirms before closing on Escape or a backdrop click. The
 * cross and the cancel action are deliberate, so they are left alone.
 *
 * @param {boolean}  open
 * @param {Function} onClose      called once the close is allowed to proceed
 * @param {string}   title        required — it is the accessible name
 * @param {Node}     actions      the footer; put the primary action last
 * @param {number}   width        content width in px (default 560)
 * @param {boolean}  dirty        guard accidental dismissal (default false)
 * @param {string}   discardMessage  what the guard asks
 * @param {'light'|'dark'} tone  which ground the dialog sits on
 *
 * ON `tone`
 *
 * Most dialogs sit on light pages and are light. The new-document dialog is a
 * dark purple panel, and that was never the defect — its problems were a
 * missing close affordance, an invisible primary action and inconsistent
 * spacing, all of which this shell fixes without repainting it. Turning it
 * white would have been a redesign wearing a bug fix's clothes.
 *
 * Both tones take every value from the same tokens, so there are two grounds
 * here, not two design systems.
 */
export default function Dialog({
  open,
  onClose,
  title,
  actions,
  children,
  width = 560,
  dirty = false,
  discardMessage = 'Discard what you have entered?',
  tone = 'light',
  ...rest
}) {
  const dark = tone === 'dark';
  const surface = {
    bg: dark ? tokens.color.purple : tokens.color.card,
    fg: dark ? '#FFFFFF' : tokens.color.ink,
    rule: dark ? tokens.color.lineOnDark : tokens.color.line,
    quiet: dark ? tokens.color.onDark : tokens.color.muted,
    hover: dark ? 'rgba(255,255,255,0.12)' : tokens.color.purpleTint,
  };
  // Escape and backdrop clicks are easy to trigger by accident; the cross and
  // the cancel button are not. Only guard the accidental ones.
  const handleClose = useCallback(
    (event, reason) => {
      const accidental = reason === 'escapeKeyDown' || reason === 'backdropClick';
      if (dirty && accidental && !window.confirm(discardMessage)) return;
      onClose?.(event, reason);
    },
    [dirty, discardMessage, onClose]
  );

  return (
    <MuiDialog
      open={open}
      onClose={handleClose}
      aria-labelledby="rv-dialog-title"
      PaperProps={{
        sx: {
          width,
          maxWidth: 'calc(100vw - 32px)',
          borderRadius: `${tokens.radius.panel}px`,
          backgroundColor: surface.bg,
          color: surface.fg,
          boxShadow: tokens.shadow.lg,
        },
      }}
      {...rest}
    >
      <DialogTitle
        id="rv-dialog-title"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          px: `${tokens.space.xl}px`,
          py: `${tokens.space.lg}px`,
          borderBottom: `1px solid ${surface.rule}`,
          fontFamily: tokens.text.heading.family,
          fontSize: tokens.text.heading.size,
          fontWeight: tokens.text.heading.weight,
          lineHeight: tokens.text.heading.lineHeight,
          color: surface.fg,
        }}
      >
        <Box component="span">{title}</Box>
        {/* The audit found no dialog in the product had one of these. */}
        <IconButton
          onClick={(e) => onClose?.(e, 'closeButton')}
          aria-label="Close"
          size="small"
          sx={{
            color: surface.quiet,
            // 44px so it clears the touch minimum the rest of Phase 4 adopts.
            width: 44,
            height: 44,
            flexShrink: 0,
            '&:hover': { color: surface.fg, backgroundColor: surface.hover },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          px: `${tokens.space.xl}px`,
          py: `${tokens.space.xl}px`,
          fontFamily: tokens.text.body.family,
          fontSize: tokens.text.body.size,
          lineHeight: tokens.text.body.lineHeight,
        }}
      >
        {children}
      </DialogContent>

      {actions && (
        <DialogActions
          sx={{
            px: `${tokens.space.xl}px`,
            py: `${tokens.space.lg}px`,
            gap: `${tokens.space.md}px`,
            borderTop: `1px solid ${surface.rule}`,
            // MUI puts its own margin between action children; the gap above
            // is the one that should win.
            '& > :not(style) ~ :not(style)': { marginLeft: 0 },
          }}
        >
          {actions}
        </DialogActions>
      )}
    </MuiDialog>
  );
}
